import { useEffect, useState, useCallback, useRef } from 'react';
import {
    createLenderRequirement,
    getBorrowerRequirements,
    getLenderRequirements,
    updateLenderRequirement,
    updateLenderRequirementsBatch,
    updateAicaChaseStatus,
    updateLenderAicaChaseStatus,
    refreshRequirements,
    addRequestForPnoConfig,
} from '../NeedInfoV2Helper';
import { useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { SOURCE, AicaChaseStatus, COMPOSE_EMAIL_SOURCE } from '../NeedInfoV2Constants';
import { getDocList } from '../../MagicUpload/MagicUploadHelper';
import { setComposeEmailModal, triggerRequirementsRefresh } from '../../../store/investor/action';
import { USER_TYPE } from '../../../utils/constants/user';
import { getPlatformUserId, getSubdomain, isEmpty } from '../../../utils/utils';
import { DISPATCH_EVENTS, HEADERS } from '../../../utils/constants/commonConstants';
import { Mixpanel } from '../../../utils/mixpanel';
import { TAB_STATE, TabState } from '../NeedInfoV2Constants';

export interface EntityRequirement {
    id: string | number;
    dataType: string;
    primaryMessage: string;
    secondaryMessage: string | null;
    link: string | null;
    source: string;
    status: 'PENDING' | 'COMPLETE';
    markedAt: string | null;
    createdAt: string;
    requirementFor?: string;
    requestedBy?: string[];
    isDeleted?: boolean;
    isArchived?: boolean;
    archivedAt?: string;
    originalQuery?: string;
    remark?: string;
    remarkUpdatedBy?: string;
    remarkUpdatedAt?: string;
    companyRequirementAudit?: AuditLogEntry[];
    lenderRequirementAudit?: AuditLogEntry[];
}

interface AuditLogEntry {
    updateMadeBy: string;
    createdAt: string;
}

const useFetchRequirements = (externalRequirementViewType: TabState = TAB_STATE.ACTIVE) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [entityRequirements, setEntityRequirements] = useState<EntityRequirement[]>([]);
    const [isAddItemDrawerOpen, setIsAddItemDrawerOpen] = useState(false);
    const params = useParams();
    const [orgId, setOrgId] = useState<string | null>(null);
    const [dataTypes, setDataTypes] = useState<string[]>([]);
    const [requestedBy, setRequestedBy] = useState<string[]>([]);
    const [lenderId, setLenderId] = useState<string | null>(null);
    const [lenderName, setLenderName] = useState<string | null>(null);
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const shouldRefreshRequirements = useAppSelector((state) => state.investor.shouldRefreshRequirements);
    const [documentList, setDocumentList] = useState([]);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string>('');
    const [lastUpdatedBy, setLastUpdatedBy] = useState<string>('');
    const [isReminderDisabled, setIsReminderDisabled] = useState(false);
    const [isAicaChaseEnabled, setIsAicaChaseEnabled] = useState(false);
    const [aicaChaseData, setAicaChaseData] = useState<{
        nextDate: string | null;
        status: AicaChaseStatus | null;
    } | null>(null);
    const [docFetching, setDocFetching] = useState(true);
    const [isAddingClarifications, setIsAddingClarifications] = useState(false);
    const [isRefreshingRequirements, setIsRefreshingRequirements] = useState(false);
    const [isRefreshRequirementsModalOpen, setIsRefreshRequirementsModalOpen] = useState(false);
    const clarificationTimerRef = useRef<NodeJS.Timeout | null>(null);
    const [isAddingBaseConfigData, setIsAddingBaseConfigData] = useState(false);
    const [activeCount, setActiveCount] = useState(0);
    const [archivedCount, setArchivedCount] = useState(0);
    const [isArchivingBatch, setIsArchivingBatch] = useState(false);

    const dispatch = useAppDispatch();

    // Always use external control - simplified approach
    const currentRequirementViewType = externalRequirementViewType;

    useEffect(() => {
        searchParams.get('assesseeId') && setOrgId(searchParams.get('assesseeId'));
        searchParams.get('lenderId') && setLenderId(searchParams.get('lenderId'));
        searchParams.get('lenderName') && setLenderName(searchParams.get('lenderName'));
        searchParams.delete('assesseeId');
        setSearchParams(searchParams);
    }, [searchParams.get('assesseeId'), searchParams.get('lenderId'), searchParams.get('lenderName')]);

    const user = useAppSelector((state) => state.user.userData);
    const isPartner = user.accountType === USER_TYPE.PARTNER;

    const getOrgId = () => {
        if (isPartner) {
            return searchParams.get('orgId') ?? params.id ?? user.assesseeOrgId ?? '';
        }
        return params.id ?? user.assesseeOrgId ?? searchParams.get('orgId') ?? '';
    };

    const getSource = () => {
        return searchParams.get('lenderReq') === 'true' ? SOURCE.LENDER_REQ : SOURCE.BORROWER_REQ;
    };

    const getRequirements = useCallback(
        async (source) => {
            setDocFetching(true);
            let res;
            if (source === SOURCE.LENDER_REQ) {
                res = await getLenderRequirements({
                    // dealId: '67f8b8156739a8066a16852b',
                    dealId: searchParams.get('dealId') as string,
                    isArchived: currentRequirementViewType === TAB_STATE.ARCHIVED,
                });
            } else if (source === SOURCE.BORROWER_REQ) {
                res = await getBorrowerRequirements({
                    orgId: getOrgId(),
                    pnoId: user.investorOrgId as string,
                    isArchived: currentRequirementViewType === TAB_STATE.ARCHIVED,
                });
            }
            if (res) {
                const requirementsWithStringIds = res.requirements.map((req: any) => ({
                    ...req,
                    id: String(req.id),
                }));
                setEntityRequirements(requirementsWithStringIds);
                setLastUpdatedAt(res.lastNotificationSentAt);
                setLastUpdatedBy(res.lastNotificationSentBy);
                setIsReminderDisabled(res.isSendReminderEnabled);
                setIsAicaChaseEnabled(res.followUpEnabled || false);
                setDataTypes(res.dataTypes || []);
                setRequestedBy(res.requestedByList || []);

                // Extract counts from API response or calculate client-side
                setActiveCount(res.activeCount || 0);
                setArchivedCount(res.archivedCount || 0);

                setAicaChaseData({
                    nextDate: res.nextFollowUpCadenceDate || null,
                    status: res.aicaChaseStatus ? AicaChaseStatus[res.aicaChaseStatus as keyof typeof AicaChaseStatus] : null,
                });
            } else {
                console.error('Failed to fetch or parse requirements:', res);
                setEntityRequirements([]);
                setActiveCount(0);
                setArchivedCount(0);
            }
            setDocFetching(false);
        },
        // Only depend on things that change the API call behavior, not triggers
        [currentRequirementViewType],
    );

    useEffect(() => {
        if (
            (searchParams.get('lenderReq') === 'true' && searchParams.get('dealId')) ||
            searchParams.get('borrowerReq') === 'true' ||
            shouldRefreshRequirements
        ) {
            getRequirements(getSource()).then(() => {
                // Reset the refresh flag after successful API call
                if (shouldRefreshRequirements) {
                    dispatch(triggerRequirementsRefresh());
                }
            });
        }
    }, [getRequirements, searchParams.get(SOURCE.LENDER_REQ), searchParams.get(SOURCE.BORROWER_REQ), shouldRefreshRequirements, dispatch]);

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '';
        try {
            // Truncate microseconds if present
            const cleanedDateString = dateString.split('.')[0];
            return dayjs(cleanedDateString).format('hh:mm A DD MMM YYYY');
        } catch (e) {
            console.error('Error formatting date:', e);
            return '';
        }
    };

    const getDocumentList = useCallback(async () => {
        if (!investorOrgId) return;
        getDocList(
            { pnoId: investorOrgId },
            {
                onSuccess: (res) => {
                    setDocumentList(res);
                },
            },
        );
    }, [investorOrgId]);

    useEffect(() => {
        getDocumentList();
    }, [getDocumentList]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (clarificationTimerRef.current) {
                clearTimeout(clarificationTimerRef.current);
            }
        };
    }, []);

    const updateRequirementOptimistically = useCallback(
        async (id: string, updates: Partial<EntityRequirement>, isDelete?: boolean) => {
            const originalRequirements = [...entityRequirements];
            const requirementIndex = originalRequirements.findIndex((req) => String(req.id) === id);

            if (requirementIndex === -1) {
                toast.error('Requirement not found for update');
                return;
            }
            // Optimistically update the UI
            const originalRequirement = originalRequirements[requirementIndex];
            if (updates.hasOwnProperty('originalQuery')) {
                updates.primaryMessage = updates.originalQuery;
            }
            const updatedRequirement = { ...originalRequirement, ...updates };

            if (isDelete) {
                // Remove the item from UI if isDelete is true
                setEntityRequirements((prev) => prev.filter((req) => String(req.id) !== id));
            } else {
                // Update the item in UI
                setEntityRequirements((prev) => prev.map((req) => (String(req.id) === id ? updatedRequirement : req)));
            }

            const numericId = Number(updatedRequirement.id);
            if (isNaN(numericId)) {
                toast.error('Could not update requirement');
                setEntityRequirements(originalRequirements);
                return;
            }

            try {
                const payload = {
                    id: numericId,
                    updatedBy: user?.userId,
                    requirementFor: getRequirementFor(),
                };
                // Include isDeleted in the updates object
                const updatedChanges = isDelete ? { ...updates } : updates;
                const success = await updateLenderRequirement({ ...payload, ...updatedChanges });

                if (!success) {
                    toast.error(isDelete ? 'Could not delete requirement' : 'Could not update requirement');
                    setEntityRequirements(originalRequirements);
                } else if (isDelete) {
                    // Refetch requirements after successful delete to update counts and ensure consistency
                    await getRequirements(getSource());
                }
                dispatchEvent(new Event(DISPATCH_EVENTS.REQUIREMENT_UPDATED));
            } catch (error) {
                console.error('Error during API update, reverting UI for ID:', id, error);
                toast.error(isDelete ? 'Could not delete requirement' : 'Could not update requirement');
                setEntityRequirements(originalRequirements);
            }
        },
        [entityRequirements, getRequirements, getSource],
    );

    const getRequirementFor = () => {
        if (searchParams.get(SOURCE.LENDER_REQ) === 'true') {
            return 'LENDER';
        } else if (searchParams.get(SOURCE.BORROWER_REQ) === 'true') {
            return 'BORROWER';
        }
        return '';
    };

    // Add Clarification

    const handleAddItemSubmit = async (clarificationText: string) => {
        const newRequirement: Partial<EntityRequirement> = {
            dataType: 'Clarification', // Set dataType
            primaryMessage: clarificationText,
            source: 'MANUAL',
            status: 'PENDING',
            requirementFor: getRequirementFor(),
        };
        let payload: any = {
            ...newRequirement,
            updatedBy: user?.userId,
            lenderDealId: searchParams.get('dealId') as string,
            pnoId: user.investorOrgId as string,
        };
        if (searchParams.get(SOURCE.BORROWER_REQ) === 'true') {
            payload.orgId = (params.id as string) || (searchParams.get('orgId') as string);
        } else {
            payload.orgId = orgId || (params.id as string);
            payload.lenderId = lenderId;
        }
        const res = await createLenderRequirement(payload);
        if (res) {
            setIsAddItemDrawerOpen(false);
            setIsAddingClarifications(true);

            // Clear any existing timer
            if (clarificationTimerRef.current) {
                clearTimeout(clarificationTimerRef.current);
            }

            // Set up timer for 45 seconds (45000 milliseconds)
            clarificationTimerRef.current = setTimeout(() => {
                setIsAddingClarifications(false);
                // Refetch requirements based on current source
                const source = searchParams.get('lenderReq') === 'true' ? SOURCE.LENDER_REQ : SOURCE.BORROWER_REQ;
                getRequirements(source);
                clarificationTimerRef.current = null;
            }, 45000);
        }
    };

    const handleRefreshRequirements = async (selectedRequirements: Set<string>, isAllSelected: boolean) => {
        setIsRefreshingRequirements(true);
        setIsRefreshRequirementsModalOpen(false);

        let payload: any = {
            companyDataRequirementIds: Array.from(selectedRequirements),
        };
        const headers = {
            [HEADERS.X_REQUESTED_ORGANIZATION_ID]: getOrgId(),
        };

        const res = await refreshRequirements(payload, headers);
        if (res) {
            toast.success('Requirements updated successfully');
            const requirementsWithStringIdsMap: { [key: string]: EntityRequirement } = {};
            for (const req of res.requirements ?? []) {
                const id = String(req.id);
                requirementsWithStringIdsMap[id] = { ...req, id: String(req.id) };
            }
            // replace the requirements with the new requirements match with id
            const newEntityRequirements: EntityRequirement[] = [];
            for (const req of entityRequirements) {
                const id = String(req.id);
                if (requirementsWithStringIdsMap[id]) {
                    const updatedReq = requirementsWithStringIdsMap[id];
                    newEntityRequirements.push(updatedReq);
                } else {
                    newEntityRequirements.push(req);
                }
            }
            setEntityRequirements(newEntityRequirements);
            dispatchEvent(new Event(DISPATCH_EVENTS.REQUIREMENT_UPDATED));
        } else {
            toast.error('failed to update requirements');
        }
        setIsRefreshingRequirements(false);

        Mixpanel.track('Need_Info_Update Duration', {
            'user Role': user?.accountType,
            'PNO Platform': getSubdomain(),
            'User Name': user?.userName,
            'Current URL': window.location.href,
            'Organization ID': params.id ?? user?.assesseeOrgId,
            'bulk selected': isAllSelected,
            'query ID': Array.from(selectedRequirements),
        });
    };

    const handleOpenRefreshRequirementsModal = () => {
        setIsRefreshRequirementsModalOpen(true);
    };

    const handleAddBaseConfigData = async () => {
        setIsAddingBaseConfigData(true);
        const res = await addRequestForPnoConfig({
            pnoId: user.investorOrgId as string,
            assesseeOrgId: getOrgId(),
        });
        if (!res) {
            toast.error('failed to generate data requests');
        }

        setIsAddingBaseConfigData(false);
        dispatchEvent(new Event(DISPATCH_EVENTS.REQUIREMENT_UPDATED));

        getRequirements(getSource());
    };

    const handleUpdateDurationModalClose = (isAllSelected: boolean, selectedRequirements: Set<string>) => {
        setIsRefreshRequirementsModalOpen(false);
        Mixpanel.track('Need_Info_Update Duration_Cancelled', {
            'user Role': user?.accountType,
            'PNO Platform': getSubdomain(),
            'User Name': user?.userName,
            'Current URL': window.location.href,
            'Organization ID': params.id ?? user?.assesseeOrgId,
            'bulk selected': isAllSelected,
            'query ID': Array.from(selectedRequirements),
        });
    };

    const handleAicaChaseStatusToggle = useCallback(
        async (newStatus: AicaChaseStatus) => {
            const isLenderRequest = searchParams.get(SOURCE.LENDER_REQ) === 'true';

            try {
                let response;

                if (isLenderRequest) {
                    // For lender side, use dealId
                    const dealId = searchParams.get('dealId');
                    if (!dealId) {
                        toast.error('Deal ID not found');
                        return null;
                    }

                    response = await updateLenderAicaChaseStatus({
                        dealId,
                        status: newStatus,
                        lastUpdatedBy: getPlatformUserId(),
                        journey: 'MANUAL_ACTION',
                    });
                } else {
                    // For borrower side, use orgId
                    const targetOrgId = params.id ?? user.assesseeOrgId ?? orgId ?? (searchParams.get('orgId') as string);
                    if (!targetOrgId) {
                        toast.error('Organization ID not found');
                        return null;
                    }

                    response = await updateAicaChaseStatus({
                        orgId: targetOrgId,
                        status: newStatus,
                        journey: 'MANUAL_ACTION',
                    });
                }

                if (response) {
                    // Update local state
                    setAicaChaseData((prev) =>
                        prev
                            ? {
                                  nextDate: response.nextCadenceDate,
                                  status: newStatus,
                              }
                            : {
                                  nextDate: null,
                                  status: newStatus,
                              },
                    );
                    // toast.success('AICA chase status updated successfully');
                    return response;
                } else {
                    return null;
                }
            } catch (error) {
                return null;
            }
        },
        [params.id, user.assesseeOrgId, orgId, searchParams, updateAicaChaseStatus, updateLenderAicaChaseStatus],
    );

    const handleOpenPortfolioPage = useCallback(() => {
        Mixpanel.track('Data Pending Viewed', {
            'user Role': user?.accountType,
            'PNO Platform': getSubdomain(),
            'User Name': user?.userName,
            'Current URL': window.location.href,
            'Organization ID': orgId,
            source: 'Lender Query',
        });
        const portfolioUrl = `${
            window.location.origin
        }/investor/app/portfolio/${orgId}?page=1&type=deals&refetchOrgInfo=true&source=deals&tab=aicaReport&selectedTab=overview&needInfo=true&showNeedInfo=true&borrowerReq=true&companyNameInfo=${searchParams.get(
            'companyName',
        )}`;
        window.open(portfolioUrl, '_blank');
    }, [user?.accountType, user?.userName, user?.assesseeOrgId, params.id, orgId, searchParams]);

    const handleArchiveBatch = useCallback(
        async (requirementIds: string[]) => {
            if (requirementIds.length === 0) {
                return { success: false, error: 'No requirements selected' };
            }

            setIsArchivingBatch(true);

            // Store original state for rollback
            const originalRequirements = [...entityRequirements];

            try {
                // Optimistically remove items from UI
                const idsToArchive = new Set(requirementIds);
                setEntityRequirements((prev) => prev.filter((req) => !idsToArchive.has(String(req.id))));

                // Convert string IDs to numbers for API
                const numericIds = requirementIds.map((id) => Number(id)).filter((id) => !isNaN(id));

                if (numericIds.length === 0) {
                    throw new Error('Invalid requirement IDs');
                }

                // Make batch API call
                const payload = {
                    ids: numericIds,
                    updatedBy: user?.userId,
                    isArchived: true,
                    requirementFor: getRequirementFor(),
                };

                const result = await updateLenderRequirementsBatch(payload);

                if (!result.success) {
                    // Revert UI on failure
                    setEntityRequirements(originalRequirements);
                    toast.error(result.error || 'Failed to archive requirements');
                    return { success: false, error: result.error };
                }

                // Success
                toast.success(
                    `Successfully archived ${requirementIds.length} ${requirementIds.length === 1 ? 'requirement' : 'requirements'}`,
                );
                dispatchEvent(new Event(DISPATCH_EVENTS.REQUIREMENT_UPDATED));

                // Track analytics
                Mixpanel.track('Batch Archive Requirements', {
                    'user Role': user?.accountType,
                    'PNO Platform': getSubdomain(),
                    'User Name': user?.userName,
                    'Current URL': window.location.href,
                    'Organization ID': params.id ?? user?.assesseeOrgId,
                    'Requirement Count': requirementIds.length,
                    'Requirement IDs': requirementIds,
                });

                // Refetch requirements after successful archive to update counts and ensure consistency
                await getRequirements(getSource());

                return { success: true };
            } catch (error) {
                console.error('Error archiving requirements in batch:', error);
                // Revert UI on error
                setEntityRequirements(originalRequirements);
                toast.error('An error occurred while archiving requirements');
                return { success: false, error: 'An error occurred' };
            } finally {
                setIsArchivingBatch(false);
            }
        },
        [entityRequirements, user, params.id, getRequirementFor, getRequirements, getSource],
    );

    // Helper functions for compose email functionality
    const isLenderRequest = searchParams.get('lenderReq') === 'true';

    const getComposeEmailButtonText = useCallback(() => {
        return isLenderRequest ? 'Email Lender' : 'Compose Email';
    }, [isLenderRequest]);

    const isComposeEmailDisabled = useCallback(
        (loading: boolean, combinedOrgUserList: any) => {
            return (
                loading ||
                (isEmpty(combinedOrgUserList.assesseeUsers) && isEmpty(combinedOrgUserList.partnerUsers) && !isLenderRequest) ||
                searchParams.get('disableSendReminder') === 'true'
            );
        },
        [isLenderRequest, searchParams],
    );

    const getOrgNameForEmail = useCallback(() => {
        // Drop missing / literal "undefined"/"null" param values (a stale redirect
        // can write companyNameInfo=undefined) so the compose header never shows
        // "undefined". Check every company-name param variant + the lender name.
        const clean = (v: string | null) => (v && v !== 'undefined' && v !== 'null' ? v : '');
        if (isLenderRequest) {
            return clean(lenderName) || clean(searchParams.get('lenderName'));
        }
        return clean(searchParams.get('companyNameInfo')) || clean(searchParams.get('companyName')) || clean(searchParams.get('orgName'));
    }, [isLenderRequest, lenderName, searchParams]);

    const handleComposeEmailClick = useCallback(
        (selectedRequirements: Set<string>) => {
            dispatch(
                setComposeEmailModal({
                    show: true,
                    selectedRequirements: Array.from(selectedRequirements),
                    orgName: getOrgNameForEmail(),
                    source: isLenderRequest ? COMPOSE_EMAIL_SOURCE.LENDER_QUERY : COMPOSE_EMAIL_SOURCE.DATA_PENDING,
                    lenderId: lenderId || '',
                }),
            );
        },
        [dispatch, getOrgNameForEmail, isLenderRequest, lenderId],
    );

    return {
        lenderRequirements: entityRequirements, // Remove client-side filtering since API now handles it
        setLenderRequirements: setEntityRequirements,
        formatDate,
        updateRequirementOptimistically,
        handleAddItemSubmit,
        isAddItemDrawerOpen,
        setIsAddItemDrawerOpen,
        documentList,
        lastUpdatedAt,
        lastUpdatedBy,
        setLastUpdatedAt,
        docFetching,
        isAicaChaseEnabled,
        aicaChaseData,
        handleAicaChaseStatusToggle,
        lenderName,
        lenderId,
        dataTypes,
        requestedBy,
        isAddingClarifications,
        orgId,
        isRefreshingRequirements,
        isRefreshRequirementsModalOpen,
        handleOpenRefreshRequirementsModal,
        handleRefreshRequirements,
        isAddingBaseConfigData,
        handleAddBaseConfigData,
        handleUpdateDurationModalClose,
        handleOpenPortfolioPage,
        activeCount,
        archivedCount,
        requirementViewType: currentRequirementViewType,
        handleArchiveBatch,
        isArchivingBatch,
        // Compose email helpers
        getComposeEmailButtonText,
        isComposeEmailDisabled,
        getOrgNameForEmail,
        handleComposeEmailClick,
        isLenderRequest,
    };
};

export default useFetchRequirements;
